import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Menu, X, LayoutDashboard, ShieldCheck, LogOut } from "lucide-react";
import { useAuth } from "../lib/auth";
import ChatWidget from "./ChatWidget";

const NAV = [["/about", "About"], ["/membership", "Membership"], ["/library", "Library"], ["/events", "Events"], ["/directory", "Directory"], ["/circles", "Circles"], ["/news", "News"], ["/contact", "Contact"]];

export const Logo = () => (
  <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo-link" aria-label="Kilnhouse Guild home">
    <span className="flex h-9 w-9 items-center justify-center rounded-t-full rounded-b-lg bg-ink font-serif text-lg text-ochre">K</span>
    <span className="font-serif text-xl text-ink">Kilnhouse <span className="italic text-celadon-dark">Guild</span></span>
  </Link>
);

function AccountLinks({ onNav }) {
  const { user, isAdmin, signOut } = useAuth();
  const nav = useNavigate();
  if (!user)
    return (
      <>
        <Link to="/login" onClick={onNav} className="btn-ghost btn-sm" data-testid="nav-login-link">Log in</Link>
        <Link to="/register" onClick={onNav} className="btn-ochre btn-sm" data-testid="nav-join-link">Join the Guild</Link>
      </>
    );
  return (
    <>
      {isAdmin && <Link to="/admin" onClick={onNav} className="btn-ghost btn-sm" data-testid="nav-admin-link"><ShieldCheck className="h-4 w-4" aria-hidden="true" />Admin</Link>}
      <Link to="/dashboard" onClick={onNav} className="btn-primary btn-sm" data-testid="nav-dashboard-link"><LayoutDashboard className="h-4 w-4" aria-hidden="true" />Dashboard</Link>
      <button onClick={async () => { await signOut(); onNav?.(); nav("/"); }} className="btn-ghost btn-sm" data-testid="nav-logout-btn"><LogOut className="h-4 w-4" aria-hidden="true" />Log out</button>
    </>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const linkCls = ({ isActive }) => `rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${isActive ? "bg-sand text-ink" : "text-[#3d424c] hover:text-ink hover:bg-sand/60"}`;
  return (
    <header className="sticky top-0 z-40 border-b border-line bg-paper/90 backdrop-blur-md">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-50 focus:rounded focus:bg-white focus:px-3 focus:py-2">Skip to content</a>
      <div className="kh-container flex h-16 items-center justify-between gap-4">
        <Logo />
        <nav aria-label="Main" className="hidden items-center gap-0.5 xl:flex">
          {NAV.map(([to, label]) => <NavLink key={to} to={to} className={linkCls} data-testid={`nav-${label.toLowerCase()}-link`}>{label}</NavLink>)}
        </nav>
        <div className="hidden items-center gap-2 xl:flex"><AccountLinks /></div>
        <button className="rounded-full p-2 xl:hidden" onClick={() => setOpen(!open)} aria-expanded={open} aria-label="Toggle menu" data-testid="nav-mobile-toggle">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <nav aria-label="Mobile" className="border-t border-line bg-paper px-4 pb-5 pt-3 xl:hidden" data-testid="nav-mobile-menu">
          <div className="grid grid-cols-2 gap-1">
            {NAV.map(([to, label]) => <NavLink key={to} to={to} onClick={() => setOpen(false)} className={linkCls} data-testid={`mobile-nav-${label.toLowerCase()}-link`}>{label}</NavLink>)}
          </div>
          <div className="mt-4 flex flex-wrap gap-2"><AccountLinks onNav={() => setOpen(false)} /></div>
        </nav>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="mt-24 bg-ink text-paper" data-testid="site-footer">
      <div className="kh-container grid gap-10 py-14 md:grid-cols-4">
        <div className="md:col-span-2">
          <p className="font-serif text-2xl text-paper">Kilnhouse <span className="italic text-celadon">Guild</span></p>
          <p className="mt-3 max-w-sm text-sm text-paper/75">Where makers, studios and galleries find each other. Staff-reviewed introductions, a working library and studio circles.</p>
        </div>
        <div>
          <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-celadon">Explore</p>
          <ul className="space-y-2 text-sm text-paper/80">
            {NAV.slice(0, 5).map(([to, l]) => <li key={to}><Link to={to} className="hover:text-ochre">{l}</Link></li>)}
          </ul>
        </div>
        <div>
          <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-celadon">Guild</p>
          <ul className="space-y-2 text-sm text-paper/80">
            <li><Link to="/news?kind=newsletter" className="hover:text-ochre">The Kiln Letter</Link></li>
            <li><Link to="/p/code-of-conduct" className="hover:text-ochre">Code of Conduct</Link></li>
            <li><Link to="/contact" className="hover:text-ochre">Contact</Link></li>
          </ul>
        </div>
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main id="main" className="flex-1"><Outlet /></main>
      <Footer />
      <ChatWidget />
    </div>
  );
}
